import { put } from 'redux-saga/effects';

import api from '../../helpers/api';
import toastr from '../../helpers/toastr';
import { store } from '../store';
import * as actions from './actions';

import {
  GetDoneApprovalEntries,
  GetDoneApprovalGroups,
  GetPendingApprovalEntries,
  GetPendingApprovalGroups,
  GetPendingApprovalSelected,
} from '@sit/client-shared';

import { DEFAULT_PAGE_SIZE_INFINITE_SCROLL } from '../../constants/pageSizes';
import { createAppHttpClient } from '../../graphql/apollo';
import { getCompanyFromWebSessionStorage } from '../../helpers/session';
import { selectToken } from '../selectors';
import {
  selectApprovalsDoneAll,
  selectApprovalsDoneTimesheet,
  selectApprovalsTodoSelected,
  selectTodoActive,
  selectTodoSidebar,
} from '../selectors/approvals';

export function* approveDeclineByTimesheetsOrEntriesAsyncRequestSaga(payload) {
  const toApprovalsTypeToast = (type) => {
    switch (type) {
      case 'approve':
        return 'Approving';
      default:
        return 'Declining';
    }
  };

  try {
    const reduxStore = store.getState();
    const res = yield api.send({
      url: `/approvals/${payload?.data?.action || 'decline'}-async`,
      method: 'PUT',
      token: selectToken(reduxStore),
      data: payload.data.data,
    });
    toastr.info(`${toApprovalsTypeToast(payload?.data?.action)} timesheets, we'll notify you when we finish.`);
    yield put(actions.approveDeclineByTimesheetsOrEntriesRequestSuccess({ res, payloadData: payload.data.data }));
  } catch (error) {
    yield put(actions.approveDeclineByTimesheetsOrEntriesRequestFailure(error));
  }
}

/**
 *      DONE
 */
export function* getDoneApprovalsAllRequestSaga(payload) {
  let apolloClient;
  try {
    const reduxStore = store.getState();
    const token = selectToken(reduxStore);
    apolloClient = yield createAppHttpClient(token);
    const { groupBy = 'employee', pageNumber = 1, pageSize = DEFAULT_PAGE_SIZE_INFINITE_SCROLL, search } = payload.data;
    const res = yield apolloClient.query({
      query: GetDoneApprovalGroups,
      variables: {
        groupBy,
        pageNumber,
        pageSize,
        search,
      },
    });
    const results = res?.data?.doneApprovalGroups?.results || [];
    const count = res?.data?.doneApprovalGroups?.count || 0;

    yield put(actions.getDoneApprovalsAllRequestSuccess({ results, count, pageNumber, pageSize, groupBy, search }));
  } catch (error) {
    yield put(actions.getDoneApprovalsAllRequestFailure({ error }));
  } finally {
    apolloClient?.stop?.();
  }
}

export function* getDoneApprovalsAllNextPageRequestSaga() {
  let apolloClient;
  try {
    const reduxStore = store.getState();
    const token = selectToken(reduxStore);
    const { pageSize, pageNumber, groupBy, search } = selectApprovalsDoneAll(reduxStore);
    apolloClient = yield createAppHttpClient(token);
    const res = yield apolloClient.query({
      query: GetDoneApprovalGroups,
      variables: {
        groupBy: groupBy || 'employee',
        pageNumber: pageNumber + 1,
        pageSize: pageSize || DEFAULT_PAGE_SIZE_INFINITE_SCROLL,
        search,
      },
    });
    const results = res?.data?.doneApprovalGroups?.results || [];

    yield put(actions.getDoneApprovalsAllNextPageRequestSuccess({ results }));
  } catch (error) {
    yield put(actions.getDoneApprovalsAllNextPageRequestFailure({ error }));
  } finally {
    apolloClient?.stop?.();
  }
}

export function* getDoneApprovalsTimesheetRequestSaga(payload) {
  let apolloClient;
  try {
    const reduxStore = store.getState();
    const token = selectToken(reduxStore);
    const companyId = (yield getCompanyFromWebSessionStorage())?.id;
    apolloClient = yield createAppHttpClient(token);
    const { timesheetId, orderBy = 'date', pageNumber = 1, pageSize = DEFAULT_PAGE_SIZE_INFINITE_SCROLL } = payload.data;
    const res = yield apolloClient.query({
      query: GetDoneApprovalEntries,
      variables: {
        companyId,
        timesheetId,
        orderBy,
        pageNumber,
        pageSize,
      },
    });
    const data = res?.data?.doneApprovalEntries || [];
    yield put(actions.getDoneApprovalsTimesheetRequestSuccess({ data, timesheetId, orderBy, pageNumber }));
  } catch (error) {
    yield put(actions.getDoneApprovalsTimesheetRequestFailure({ error }));
  } finally {
    apolloClient?.stop?.();
  }
}

export function* getDoneApprovalsTimesheetNextPageRequestSaga() {
  let apolloClient;
  try {
    const reduxStore = store.getState();
    const token = selectToken(reduxStore);
    const companyId = (yield getCompanyFromWebSessionStorage())?.id;
    const { pageSize, pageNumber, timesheetId, orderBy } = selectApprovalsDoneTimesheet(reduxStore);

    apolloClient = yield createAppHttpClient(token);
    const res = yield apolloClient.query({
      query: GetDoneApprovalEntries,
      variables: {
        companyId,
        timesheetId,
        orderBy,
        pageNumber: pageNumber + 1,
        pageSize: pageSize || DEFAULT_PAGE_SIZE_INFINITE_SCROLL,
      },
    });

    const data = res?.data?.doneApprovalEntries || [];

    yield put(actions.getDoneApprovalsTimesheetNextPageRequestSuccess({ data, timesheetId, orderBy, pageNumber }));
  } catch (error) {
    yield put(actions.getDoneApprovalsTimesheetNextPageRequestFailure({ error }));
  } finally {
    apolloClient?.stop?.();
  }
}

/**
 *      TODO
 */
export function* getTodoApprovalsGroupsRequestSaga(payload) {
  let apolloClient;
  try {
    const reduxStore = store.getState();
    const token = selectToken(reduxStore);
    const companyId = (yield getCompanyFromWebSessionStorage())?.id;
    apolloClient = yield createAppHttpClient(token);
    const { groupBy = 'employee', pageNumber = 1, pageSize = DEFAULT_PAGE_SIZE_INFINITE_SCROLL, search } = payload.data;
    const res = yield apolloClient.query({
      query: GetPendingApprovalGroups,
      variables: {
        companyId,
        groupBy,
        pageNumber,
        pageSize,
        search,
      },
    });
    const groups = res?.data?.pendingApprovalGroups?.groups || [];
    const count = res?.data?.pendingApprovalGroups?.count || 0;

    yield put(actions.getTodoApprovalsGroupsRequestSuccess({ groups, count, pageNumber, pageSize, groupBy, search }));
  } catch (error) {
    yield put(actions.getTodoApprovalsGroupsRequestFailure({ error }));
  } finally {
    apolloClient?.stop?.();
  }
}

export function* getTodoApprovalsGroupsNextPageRequestSaga() {
  let apolloClient;
  try {
    const reduxStore = store.getState();
    const token = selectToken(reduxStore);
    const companyId = (yield getCompanyFromWebSessionStorage())?.id;
    const { pageSize, pageNumber, groupBy, search } = selectTodoSidebar(reduxStore);
    apolloClient = yield createAppHttpClient(token);
    const res = yield apolloClient.query({
      query: GetPendingApprovalGroups,
      variables: {
        companyId,
        groupBy: groupBy || 'employee',
        pageNumber: pageNumber + 1,
        pageSize: pageSize || DEFAULT_PAGE_SIZE_INFINITE_SCROLL,
        search,
      },
    });
    const groups = res?.data?.pendingApprovalGroups?.groups || [];

    yield put(actions.getTodoApprovalsGroupsNextPageRequestSuccess({ groups }));
  } catch (error) {
    yield put(actions.getTodoApprovalsGroupsNextPageRequestFailure({ error }));
  } finally {
    apolloClient?.stop?.();
  }
}

export function* getTodoApprovalsActiveRequestSaga(payload) {
  let apolloClient;
  try {
    const reduxStore = store.getState();
    const token = selectToken(reduxStore);
    const companyId = (yield getCompanyFromWebSessionStorage())?.id;
    apolloClient = yield createAppHttpClient(token);
    const { groupBy = 'employee', groupId, orderBy = 'date', pageNumber = 1, pageSize = DEFAULT_PAGE_SIZE_INFINITE_SCROLL } = payload.data;
    const res = yield apolloClient.query({
      query: GetPendingApprovalEntries,
      variables: {
        companyId,
        groupBy,
        groupId,
        orderBy,
        pageNumber,
        pageSize,
      },
    });
    const data = res?.data?.pendingApprovalEntries || [];
    yield put(actions.getTodoApprovalsActiveRequestSuccess({ data, groupBy, groupId, orderBy, pageNumber, pageSize }));
  } catch (error) {
    yield put(actions.getTodoApprovalsActiveRequestFailure({ error, groupBy: payload.data?.groupBy }));
  } finally {
    apolloClient?.stop?.();
  }
}

export function* getTodoApprovalsActiveNextPageRequestSaga() {
  let apolloClient;
  try {
    const reduxStore = store.getState();
    const token = selectToken(reduxStore);
    const companyId = (yield getCompanyFromWebSessionStorage())?.id;
    const { pageSize, pageNumber, groupBy, groupId, orderBy } = selectTodoActive(reduxStore);
    apolloClient = yield createAppHttpClient(token);
    const res = yield apolloClient.query({
      query: GetPendingApprovalEntries,
      variables: {
        companyId,
        orderBy,
        groupBy: groupBy || 'employee',
        groupId,
        pageNumber: pageNumber + 1,
        pageSize: pageSize || DEFAULT_PAGE_SIZE_INFINITE_SCROLL,
      },
    });
    const groups = res?.data?.pendingApprovalEntries.groups || [];

    yield put(actions.getTodoApprovalsActiveNextPageRequestSuccess({ groups, orderBy }));
  } catch (error) {
    yield put(actions.getTodoApprovalsActiveNextPageRequestFailure({ error }));
  } finally {
    apolloClient?.stop?.();
  }
}

export function* getTodoApprovalsSelectedRequestSaga(payload) {
  let apolloClient;
  try {
    const reduxStore = store.getState();
    const token = selectToken(reduxStore);
    apolloClient = yield createAppHttpClient(token);
    const { groupBy = 'employee', groupIds, pageNumber = 1, pageSize = DEFAULT_PAGE_SIZE_INFINITE_SCROLL } = payload.data;
    const companyId = (yield getCompanyFromWebSessionStorage())?.id;
    const res = yield apolloClient.query({
      query: GetPendingApprovalSelected,
      variables: {
        companyId,
        groupBy,
        groupIds,
        pageNumber,
        pageSize,
      },
    });
    const data = res?.data?.pendingApprovalSelected || [];
    yield put(actions.getTodoApprovalsSelectedRequestSuccess({ data, groupBy, groupIds, pageNumber, pageSize }));
  } catch (error) {
    yield put(actions.getTodoApprovalsSelectedRequestFailure({ error, groupBy: payload.data?.groupBy }));
  } finally {
    apolloClient?.stop?.();
  }
}

export function* getTodoApprovalsSelectedNextPageRequestSaga() {
  let apolloClient;
  try {
    const reduxStore = store.getState();
    const token = selectToken(reduxStore);
    const companyId = (yield getCompanyFromWebSessionStorage())?.id;
    const { pageSize, pageNumber, groupBy, groupIds } = selectApprovalsTodoSelected(reduxStore);
    apolloClient = yield createAppHttpClient(token);
    const res = yield apolloClient.query({
      query: GetPendingApprovalSelected,
      variables: {
        companyId,
        groupBy: groupBy || 'employee',
        groupIds,
        pageNumber: pageNumber + 1,
        pageSize: pageSize || DEFAULT_PAGE_SIZE_INFINITE_SCROLL,
      },
    });
    const groups = res?.data?.pendingApprovalSelected.groups || [];

    yield put(actions.getTodoApprovalsSelectedNextPageRequestSuccess({ groups }));
  } catch (error) {
    yield put(actions.getTodoApprovalsSelectedNextPageRequestFailure({ error }));
  } finally {
    apolloClient?.stop?.();
  }
}
